import { Typography, Steps, Alert, Tag } from 'antd';
import {
  FileTextOutlined,
  EditOutlined,
  SendOutlined,
  BulbOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

function InstructionsContent() {
  return (
    <div className="instructions-content">
      <Paragraph>
        This is a <Text strong>scenario-based simulation</Text> designed to
        assess how you think, make decisions, and respond to real-world
        workplace situations. There are no trick questions — the goal is to
        understand your approach and reasoning.
      </Paragraph>

      <Title level={5}>How the simulation works</Title>
      <Steps
        direction="vertical"
        size="small"
        current={-1}
        items={[
          {
            title: 'Read the scenario',
            description:
              'You will be placed in a work context with a narrative description and supporting artifacts (documents, data, forms, etc.). Take time to read and understand the situation before acting.',
            icon: <FileTextOutlined />,
          },
          {
            title: 'Describe your action',
            description:
              'For each turn, write what you would do next in the situation. Be specific — describe your action clearly rather than speaking in vague terms. The more detail you provide, the better the simulation can respond.',
            icon: <EditOutlined />,
          },
          {
            title: 'Add your reflection (optional but recommended)',
            description:
              'Use the reflection box to explain your reasoning — why you chose this action, what risks you considered, and any assumptions you are making. Reflections significantly improve your evaluation.',
            icon: <BulbOutlined />,
          },
          {
            title: 'Submit and progress',
            description:
              'After submitting a turn, the scenario evolves based on your action. New information, artifacts, or unexpected events may appear. Continue until the simulation concludes.',
            icon: <SendOutlined />,
          },
        ]}
      />

      <Title level={5} style={{ marginTop: 20 }}>
        What to expect during the simulation
      </Title>
      <ul className="instructions-list">
        <li>
          <Text strong>Multiple turns:</Text> The simulation progresses over
          several turns. Each turn represents a decision point in the scenario.
        </li>
        <li>
          <Text strong>Artifacts:</Text> You may be given documents, data
          tables, forms, or timelines. Review them carefully — they often
          contain important context clues.
        </li>
        <li>
          <Text strong>Unexpected events:</Text>{' '}
          <Tag color="warning" style={{ fontSize: 12 }}>
            Mutation Events
          </Tag>{' '}
          may introduce surprises mid-simulation, such as an escalation or new
          information. Stay adaptable.
        </li>
        <li>
          <Text strong>Status indicators:</Text> A feedback panel shows how
          your decisions are tracking across evaluated dimensions. Use it as a
          guide, not a score.
        </li>
        <li>
          <Text strong>Time warnings:</Text> You will be notified when you are
          approaching the end of allowed turns.
        </li>
        <li>
          <Text strong>Inactivity:</Text> If you are inactive for an extended
          period the session may pause. You will be able to resume from where
          you left off.
        </li>
      </ul>

      <Alert
        type="info"
        icon={<ExclamationCircleOutlined />}
        showIcon
        style={{ marginTop: 16 }}
        message="Tips for doing well"
        description={
          <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
            <li>Read all artifacts before deciding on an action.</li>
            <li>
              Describe actions in first person ("I would…") with concrete
              next steps.
            </li>
            <li>
              Use reflections to show your thinking — they are evaluated
              separately and carry weight.
            </li>
            <li>
              Treat the simulation like a real work situation, not a test with a
              single correct answer.
            </li>
            <li>Stay focused — avoid switching tabs or leaving the page.</li>
          </ul>
        }
      />
    </div>
  );
}

export default InstructionsContent;
